import { Component, inject, OnDestroy, OnInit, signal, computed } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd } from '@angular/router';
import { IButton } from '@devinforius/i-essentials';
import { BootstrapCommonIcons } from '@shared/enums';
import { ClassificationFormGroup } from '@shared/interfaces/classifications';
import { AldermanshipDto } from '@shared/interfaces/dtos/aldermanships';
import { DecisionTypeDto } from '@shared/interfaces/dtos/decision-types';
import { EmergencyDto } from '@shared/interfaces/dtos/emergencies';
import { MeetingTypeDto } from '@shared/interfaces/dtos/global';
import { MailDto } from '@shared/interfaces/dtos/mails';
import { MatterDto } from '@shared/interfaces/dtos/matters';
import { PointParametersDto, PointStatusDto, PointTypeDto } from '@shared/interfaces/dtos/points';
import { ServiceDto } from '@shared/interfaces/dtos/services';
import { PointFormGroup } from '@shared/interfaces/points/point-formgroup.interface';
import { BusinessRouter } from '@shared/models/dev';
import { MyRightsForPoints } from '@shared/models/rights';
import { MailsService } from '@shared/services/mails.service';
import { MyOwnRightsService } from '@shared/services/my-own-rights.service';
import { ServicesService } from '@shared/services/services.service';
import { FormValidators } from '@shared/utils';
import { PointsMapper } from '@shared/utils/points-mapper.util';
import { TreeNode } from 'primeng/api';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { filter, finalize, map, startWith, Subscription } from 'rxjs';
import { FormOptions } from 'src/app/common/models/form-options';
import { NotificationError } from 'src/app/core/models';
import { Notifier } from 'src/app/core/services';
import { Translater } from 'src/app/core/services/translater';
import { Meeting, MeetingDetail } from 'src/app/shared/interfaces/meetings';
import { PointDetail } from 'src/app/shared/interfaces/points';
import { MeetingsService } from 'src/app/shared/services/meetings.service';
import { PointsService } from 'src/app/shared/services/points.service';
import { PointFormMode } from 'src/app/shared/types/points';

@Component({
  selector: 'app-point-form',
  templateUrl: './point-form.component.html',
  styleUrl: './point-form.component.scss',
})
export class PointFormComponent implements OnInit, OnDestroy {
  private readonly meetingsService = inject(MeetingsService);
  private readonly pointsService = inject(PointsService);
  private readonly mailsService = inject(MailsService);
  private readonly servicesService = inject(ServicesService);
  private readonly rightsService = inject(MyOwnRightsService);
  private readonly titleService = inject(Title);
  private readonly formOptions = inject(FormOptions);
  private readonly translater = inject(Translater);
  private readonly route = inject(ActivatedRoute);
  private readonly businessRouter = inject(BusinessRouter);
  private readonly notifier = inject(Notifier);

  public readonly currentMeeting = signal<MeetingDetail | undefined>(undefined);
  public readonly currentPoint = signal<PointDetail | undefined>(undefined);
  public readonly currentMail = signal<MailDto | undefined>(undefined);
  public readonly currentMode = signal<PointFormMode>('create');
  public readonly buttons = signal<IButton[]>([]);

  public readonly title = computed<string>(() => this.getTitle());

  public readonly fetchingMeeting = signal<boolean>(false);
  public readonly fetchingPoint = signal<boolean>(false);
  public readonly fetchingMail = signal<boolean>(false);
  public readonly fetchingRights = signal<boolean>(false);
  public readonly fetchingMeetings = signal<boolean>(false);

  public readonly loading = computed<boolean>(
    () => this.fetchingMeeting() || this.fetchingPoint() || this.fetchingMail() || this.fetchingRights()
  );

  public readonly rights = signal<MyRightsForPoints>(new MyRightsForPoints());

  public readonly pointStatusesOptions = signal<PointStatusDto[]>([]);
  public readonly meetingTypesOptions = signal<MeetingTypeDto[]>([]);
  public readonly meetingsOptions = signal<Meeting[]>([]);
  public readonly servicesOptions = signal<TreeNode<ServiceDto>[]>([]);
  public readonly mattersOptions = signal<MatterDto[]>([]);
  public readonly aldermanshipsOptions = signal<AldermanshipDto[]>([]);
  public readonly pointTypesOptions = signal<PointTypeDto[]>([]);
  public readonly decisionTypesOptions = signal<DecisionTypeDto[]>([]);
  public readonly emergenciesOptions = signal<EmergencyDto[]>([]);

  public readonly form = new FormGroup<PointFormGroup>({
    title: new FormControl<string | null>(null, [Validators.required, FormValidators.notEmptyString()]),
    status: new FormControl<PointStatusDto | null>(null, Validators.required),
    meetingType: new FormControl<MeetingTypeDto | null>(null, Validators.required),
    meeting: new FormControl<Meeting | null>(null, Validators.required),
    service: new FormControl<TreeNode<ServiceDto> | null>(null, Validators.required),
    orderInService: new FormControl<string | null>(null),
    matter: new FormControl<MatterDto | null>(null),
    aldermanship: new FormControl<AldermanshipDto | null>(null),
    pointType: new FormControl<PointTypeDto | null>(null),
    isConfidential: new FormControl<boolean>(false),
    withRGPDControl: new FormControl<boolean>(false),
    origin: new FormControl<string | null>(null),
    linkedMailId: new FormControl<number | null>(null),
    agent: new FormControl<any | null>(null),
    decisionType: new FormControl<DecisionTypeDto | null>(null),
    emergency: new FormControl<EmergencyDto | null>(null),
    classification: new FormControl<ClassificationFormGroup | null>(null),
  });
  private readonly subs: Subscription[] = [];

  public get isAldermanshipRequired(): boolean {
    return this.form.get('aldermanship')?.hasValidator(Validators.required) ?? false;
  }

  public get isClassificationRequired(): boolean {
    return this.form.get('classification')?.hasValidator(Validators.required) ?? false;
  }

  public get isMatterRequired(): boolean {
    return this.form.get('matter')?.hasValidator(Validators.required) ?? false;
  }

  public get isOriginRequired(): boolean {
    return this.form.get('origin')?.hasValidator(Validators.required) ?? false;
  }

  public get isDecisionTypeRequired(): boolean {
    return this.form.get('decisionType')?.hasValidator(Validators.required) ?? false;
  }

  public get isEmergencyRequired(): boolean {
    return this.form.get('emergency')?.hasValidator(Validators.required) ?? false;
  }

  public get meetingDropdownPlaceholder(): string {
    const isMeetingTypeSelected = !!this.form.get('meetingType')?.value;

    if (!isMeetingTypeSelected) {
      return this.translater.translates('POINTS.FORM.PLACEHOLDERS.SELECT_A_MEETING_TYPE');
    }

    return '';
  }

  ngOnInit(): void {
    this.initRouteSub();
    this.initRights();
    this.initRequiredFields();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromAll();
  }

  private initRequiredFields(): void {
    this.pointsService.fetchPointParameters().subscribe({
      next: (parameters: PointParametersDto) => {
        const {
          echevinatObligatoire,
          classementObligatoire,
          matiereObligatoire,
          origineObligatoire,
          typeDecisionObligatoire,
          urgenceObligatoire,
        } = parameters;

        if (echevinatObligatoire) {
          this.form.get('aldermanship')?.setValidators(Validators.required);
        }

        if (classementObligatoire) {
          this.form.get('classification')?.setValidators(Validators.required);
        }

        if (matiereObligatoire) {
          this.form.get('matter')?.setValidators(Validators.required);
        }

        if (origineObligatoire) {
          this.form.get('origin')?.setValidators(Validators.required);
        }

        if (typeDecisionObligatoire) {
          this.form.get('decisionType')?.setValidators(Validators.required);
        }

        if (urgenceObligatoire) {
          this.form.get('emergency')?.setValidators(Validators.required);
        }
      },
    });
  }

  private deactivateDynamicFields(): void {
    this.form.get('meeting')?.disable();
  }

  private deactivateFieldsDependingOnRights(): void {
    if (this.rights().cannot('changeStatus')) {
      this.form.get('status')?.disable();
    }

    if (this.rights().cannot('changePointType')) {
      this.form.get('pointType')?.disable();
    }

    if (this.rights().cannot('changeMatter')) {
      this.form.get('matter')?.disable();
    }

    if (this.rights().cannot('changeAldermanship')) {
      this.form.get('aldermanship')?.disable();
    }
  }

  private toggleAFormControl(control: AbstractControl, shouldBeEnabled: boolean): void {
    if (shouldBeEnabled) {
      control.enable();
    } else {
      control.disable();
    }
  }

  private setFormState(): void {
    const meetingTypeControl = this.form.controls.meetingType;
    const meetingControl = this.form.controls.meeting;

    const sub = meetingTypeControl.valueChanges.pipe(startWith(meetingTypeControl.value)).subscribe((meeting) => {
      this.toggleAFormControl(meetingControl, !!meeting);
    });

    this.subs.push(sub);
  }

  /**
   * @description logs the access to the point form with the query params for debugging purposes
   */
  private logRouteAccess({
    mailId,
    meetingId,
    pointId,
    mode,
  }: {
    mailId: string | null;
    meetingId: string | null;
    pointId: string | null;
    mode: PointFormMode;
  }): void {
    console.log(
      `Accessing to point form with query params : mailId=${mailId}, meetingId=${meetingId}, pointId=${pointId} in mode ${mode}`
    );
  }

  private verifyRouteQueryParamsIntegrity({
    meetingId,
    pointId,
    mailId,
    mode,
  }: {
    meetingId: string | null;
    pointId: string | null;
    mailId: string | null;
    mode: PointFormMode;
  }): void {
    if (!mode) {
      throw new Error(
        `Missing required query param 'mode' with value in ['create', 'update', 'duplicate', 'createFromICourrier']`
      );
    }

    if (mode === 'create' && (meetingId || pointId)) {
      throw new Error(`Invalid query params for 'create' mode : meetingId and pointId should not be provided`);
    }

    if (meetingId && (isNaN(+meetingId) || +meetingId <= 0)) {
      throw new Error(`Invalid 'meetingId' query param : ${meetingId}`);
    }

    if (pointId && (isNaN(+pointId) || +pointId <= 0)) {
      throw new Error(`Invalid 'pointId' query param : ${pointId}`);
    }

    if (mailId && (isNaN(+mailId) || +mailId <= 0)) {
      throw new Error(`Invalid 'mailId' query param : ${mailId}`);
    }
  }

  private getTitle(): string {
    let title: string = '';

    const currentMode = this.currentMode();

    if (!currentMode) return 'Formulaire de point';

    switch (currentMode) {
      case 'create':
        title = this.translater.translates('POINTS.FORM.TITLE.CREATE');
        break;
      case 'update':
        title = this.translater.translates('POINTS.FORM.TITLE.EDIT');
        break;
      case 'duplicate':
        title = this.translater.translates('POINTS.FORM.TITLE.DUPLICATE');
        break;
      case 'createFromICourrier':
        title = this.translater.translates('POINTS.FORM.TITLE.CREATE_FROM_ICOURRIER');
        break;
      default:
        title = 'Formulaire de point';
        break;
    }

    this.titleService.setTitle(title);

    return title;
  }

  private initRouteSub(): void {
    const sub = this.route.queryParamMap.subscribe((paramMap) => {
      const meetingId = paramMap.get('meetingId');
      const pointId = paramMap.get('pointId');
      const mailId = paramMap.get('mailId');
      const mode = paramMap.get('mode') as PointFormMode;

      this.currentMode.set(mode);

      this.logRouteAccess({ mailId, meetingId, pointId, mode });
      this.verifyRouteQueryParamsIntegrity({ meetingId, pointId, mailId, mode });

      if (meetingId) {
        this.fetchMeeting(+meetingId);
      }

      if (pointId) {
        this.fetchPoint(+pointId);
      }

      if (mailId) {
        this.fetchMail(+mailId);
      }
    });

    this.setButtons();
    this.setFormState();

    this.subs.push(sub);
  }

  private getCreateButtons(): IButton[] {
    return [
      {
        text: this.translater.translates('POINTS.FORM.BUTTONS.USE_LIB_MODEL'),
        icon: 'bi bi-collection-fill',
        iconPosition: 'left',
        styleClass: 'blue',
        click: () => {
          //TODO implement the use of a model from the library
        },
      },
      {
        text: this.translater.translates('POINTS.FORM.BUTTONS.CREATE'),
        icon: BootstrapCommonIcons.ADD,
        styleClass: 'green',
        click: () => {
          //TODO implement the creation of a point and redirection to the next step of the creation process
        },
      },
    ];
  }

  private getUpdateButtons(): IButton[] {
    return [
      {
        text: this.translater.translates('POINTS.FORM.BUTTONS.UPDATE'),
        icon: BootstrapCommonIcons.EDIT,
        iconPosition: 'left',
        styleClass: 'green',
        click: () => {
          //TODO implement the update of the point and redirection to the meeting detail page
        },
      },
    ];
  }

  private getDuplicateButtons(): IButton[] {
    return [
      {
        text: this.translater.translates('POINTS.FORM.BUTTONS.DUPLICATE'),
        icon: BootstrapCommonIcons.ADD,
        iconPosition: 'left',
        styleClass: 'green',
        click: () => {
          //TODO implement the duplication of the point and redirection to the meeting detail page of the duplicated point
        },
      },
    ];
  }
  private getCreateFromAMailButtons(): IButton[] {
    return [
      {
        text: this.translater.translates('POINTS.FORM.BUTTONS.CREATE'),
        icon: BootstrapCommonIcons.ADD,
        iconPosition: 'left',
        styleClass: 'green',
        click: () => {
          //TODO implement the creation of a point from a mail and redirection to the meeting detail page of the created point
        },
      },
    ];
  }

  private setButtons(): void {
    const mode = this.currentMode();

    switch (mode) {
      case 'create':
        this.buttons.set(this.getCreateButtons());
        break;
      case 'update':
        this.buttons.set(this.getUpdateButtons());
        break;
      case 'duplicate':
        this.buttons.set(this.getDuplicateButtons());
        break;
      case 'createFromICourrier':
        this.buttons.set(this.getCreateFromAMailButtons());
        break;
    }
  }

  private initRights(): void {
    this.fetchingRights.set(true);
    this.rightsService
      .fetchMyOwnRightsForPoints()
      .pipe(finalize(() => this.fetchingRights.set(false)))
      .subscribe({
        next: (rights) => {
          this.rights.set(rights);
          this.initOptions();
          this.deactivateDynamicFields();
          this.deactivateFieldsDependingOnRights();
        },
        error: () =>
          this.notifier.showError(
            new NotificationError({ message: this.translater.translates('POINTS.FORM.ERRORS.FETCH.RIGHTS') })
          ),
      });
  }

  private filterPointStatusesByAllowabilityAndUsability(statuses: PointStatusDto[]): PointStatusDto[] {
    const currentMode = this.currentMode();
    const currentPoint = this.currentPoint();

    const allowedStatusesIdsToChange = this.rights().allowedStatusesIdsToChange();
    const allowedStatusesIdsToView = this.rights().allowedStatusesIdsToView();

    if (currentMode === 'update' && currentPoint?.status) {
      const isPointStatusAllowed = allowedStatusesIdsToChange.includes(currentPoint.status.id);

      if (!isPointStatusAllowed && !allowedStatusesIdsToView.includes(currentPoint.status.id)) {
        allowedStatusesIdsToChange.push(currentPoint.status.id);
      }
    }

    return currentMode === 'update'
      ? statuses.filter((status) => status.utilisable && allowedStatusesIdsToChange.includes(status.id))
      : statuses.filter((status) => status.utilisable && allowedStatusesIdsToView.includes(status.id));
  }

  private filterPointTypesByAllowabilityAndUsability(pointTypes: PointTypeDto[]): PointTypeDto[] {
    const currentMode = this.currentMode();
    const currentPoint = this.currentPoint();

    const allowedPointTypeIdsToChange = this.rights().allowedPointTypeIdsToChange();
    const allowedPointTypeIdsToView = this.rights().allowedPointTypeIdsToView();

    if (currentMode === 'update' && currentPoint?.status) {
      const isPointStatusAllowed = allowedPointTypeIdsToChange.includes(currentPoint.status.id);

      if (!isPointStatusAllowed && !allowedPointTypeIdsToView.includes(currentPoint.status.id)) {
        allowedPointTypeIdsToChange.push(currentPoint.status.id);
      }
    }

    return currentMode === 'update'
      ? pointTypes.filter((status) => status.utilisable && allowedPointTypeIdsToChange.includes(status.id))
      : pointTypes.filter((status) => status.utilisable && allowedPointTypeIdsToView.includes(status.id));
  }

  private filterMattersByAllowabilityAndUsability(matters: MatterDto[]): MatterDto[] {
    const currentMode = this.currentMode();
    const currentPoint = this.currentPoint();

    const allowedMatterIdsToChange = this.rights().allowedMattersIdsToChange();
    const allowedMatterIdsToView = this.rights().allowedMattersIdsToView();

    if (currentMode === 'update' && currentPoint?.status) {
      const isPointStatusAllowed = allowedMatterIdsToChange.includes(currentPoint.status.id);

      if (!isPointStatusAllowed && !allowedMatterIdsToView.includes(currentPoint.status.id)) {
        allowedMatterIdsToChange.push(currentPoint.status.id);
      }
    }

    return currentMode === 'update'
      ? matters.filter((matter) => matter.utilisable && allowedMatterIdsToChange.includes(matter.id))
      : matters.filter((matter) => matter.utilisable && allowedMatterIdsToView.includes(matter.id));
  }

  private filterAldermanshipsByAllowabilityAndUsability(aldermanships: AldermanshipDto[]): AldermanshipDto[] {
    const currentMode = this.currentMode();
    const currentPoint = this.currentPoint();

    const allowedAldermanshipIdsToChange = this.rights().allowedAldermanIdsToChange();
    const allowedAldermanshipIdsToView = this.rights().allowedAldermanIdsToView();

    if (currentMode === 'update' && currentPoint?.status) {
      const isPointStatusAllowed = allowedAldermanshipIdsToChange.includes(currentPoint.status.id);

      if (!isPointStatusAllowed && !allowedAldermanshipIdsToView.includes(currentPoint.status.id)) {
        allowedAldermanshipIdsToChange.push(currentPoint.status.id);
      }
    }

    return currentMode === 'update'
      ? aldermanships.filter(
          (aldermanship) => aldermanship.utilisable && allowedAldermanshipIdsToChange.includes(aldermanship.id)
        )
      : aldermanships.filter(
          (aldermanship) => aldermanship.utilisable && allowedAldermanshipIdsToView.includes(aldermanship.id)
        );
  }

  private initOptions(): void {
    this.pointStatusesOptions.set(this.filterPointStatusesByAllowabilityAndUsability([]));
    this.pointTypesOptions.set(this.filterPointTypesByAllowabilityAndUsability([]));
    this.mattersOptions.set(this.filterMattersByAllowabilityAndUsability([]));
    this.aldermanshipsOptions.set(this.filterAldermanshipsByAllowabilityAndUsability(this.formOptions.aldermanships()));
    this.meetingTypesOptions.set([]);

    if (this.currentMode() === 'duplicate' || this.currentMode() === 'update') {
      this.populateForm();
    }
  }

  private populateForm(): void {}

  private unsubscribeFromAll(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  private fetchMeeting(meetingId: number): void {
    if (this.fetchingMeeting()) return;

    this.fetchingMeeting.set(true);
    this.meetingsService
      .fetchOneMeeting(meetingId)
      .pipe(finalize(() => this.fetchingMeeting.set(false)))
      .subscribe({
        next: (meetingDetail) => this.currentMeeting.set(meetingDetail),
        error: () =>
          this.notifier.showError(
            new NotificationError({ message: this.translater.translates('POINTS.FORM.ERRORS.FETCH.MEETING') })
          ),
      });
  }

  private fetchPoint(pointId: number): void {
    if (this.fetchingPoint()) return;

    // this.fetchingPoint.set(true);
    // this.pointsService
    //   .fetchOnePoint(pointId)
    //   .pipe(
    //     map((res) => PointsMapper.toDetail(res, [this.formOptions, this.servicesService])),
    //     finalize(() => this.fetchingPoint.set(false))
    //   )
    //   .subscribe({
    //     next: (pointDetail) => this.currentPoint.set(pointDetail),
    //     error: (err) => {
    //       console.info(err);
    //       this.notifier.showError(
    //         new NotificationError({ message: this.translater.translates('POINTS.FORM.ERRORS.FETCH.POINT') })
    //       );
    //     },
    //   });
  }

  private fetchMail(mailId: number): void {
    if (this.fetchingMail()) return;

    this.fetchingMail.set(true);
    this.mailsService
      .fetchOneMail(mailId)
      .pipe(finalize(() => this.fetchingMail.set(false)))
      .subscribe({
        next: (mailDetail) => this.currentMail.set(mailDetail),
        error: () =>
          this.notifier.showError(
            new NotificationError({ message: this.translater.translates('POINTS.FORM.ERRORS.FETCH.MAIL') })
          ),
      });
  }

  private fetchMeetingsByMeetingType(meetingTypeId: number): void {
    if (this.fetchingMeetings()) return;

    this.form.get('meeting')?.reset();
    this.form.get('meeting')?.disable();

    this.fetchingMeetings.set(true);
    this.meetingsService
      .fetchMeetings({ TypeSeanceIds: [meetingTypeId] })
      .pipe(finalize(() => this.fetchingMeetings.set(false)))
      .subscribe({
        next: (meetings) => {
          this.meetingsOptions.set(meetings);
          this.form.get('meeting')?.enable();
        },
        error: () =>
          this.notifier.showError(
            new NotificationError({ message: this.translater.translates('POINTS.FORM.ERRORS.FETCH.MEETINGS') })
          ),
      });
  }

  private setTypeFromMeetingTypeOrFromUpdatedPoint(): void {
    const currentMeeting = this.currentMeeting();
    const currentPoint = this.currentPoint();

    const meetingTypeToUse = this.meetingTypesOptions().find((type) => type.id === currentMeeting?.meetingTypeId);

    if (!currentMeeting || !meetingTypeToUse || meetingTypeToUse) return;

    // const pointTypeToSelect = this.pointTypesOptions().find(
    //   (type) => type.id === meetingTypeToUse.defautObjetTypeId
    // );

    // if (!pointTypeToSelect) return;

    // this.form.patchValue({
    //   pointType: pointTypeToSelect,
    // });

    // if (selectedMeetingType.objetTypeNonModifiable && selectedMeetingType.defautObjetTypeId) {
    //   this.form.get('pointType')?.disable();
    //   return;
    // }

    this.form.get('pointType')?.enable();
  }

  public onMeetingTypeChange(event: DropdownChangeEvent): void {
    const selectedMeetingType = event.value as MeetingTypeDto;

    this.form.get('pointType')?.enable();
    this.setTypeFromMeetingTypeOrFromUpdatedPoint();
    this.fetchMeetingsByMeetingType(selectedMeetingType.id);
  }

  public cancel(): void {
    this.businessRouter.cancelPointCreationOrUpdate();
  }
}
